import React, { memo, useCallback, useMemo } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import RemotePodLoader from "../components/recording/RemotePodLoader";
import SixMinuteWalkIntroCard from "../components/recording/sixMinuteWalk/SixMinuteWalkIntroCard";
import SixMinuteWalkWalkStage from "../components/recording/sixMinuteWalk/SixMinuteWalkWalkStage";
import { useSixMinuteWalkAssessment } from "../hooks/useSixMinuteWalkAssessment";
import { trackEvent } from "../utils/analytics/track";
import {
  getStatusValue,
  getUploadId,
  hasActivePostRecordingResume,
  hasPostRecordingContinueIntent,
  resolveResumePath,
} from "../utils/recording/sixMinuteWalkAssessment";
import {
  POST_WALK_PHASE_SCHEDULE,
  POST_WALK_RECORDING_DURATION_SECONDS,
  PRE_WALK_PHASE_SCHEDULE,
  PRE_WALK_RECORDING_DURATION_SECONDS,
  SIX_MINUTE_WALK_BASE_PATH,
} from "../utils/recording/sixMinuteWalkConfig";
import { RECORDING_MODES } from "../utils/recordingModes";

const loadRecordingPod = () => import("recordingPod/App");

export default memo(function SixMinuteWalkFlowPage({
  auth,
  storage,
  db,
  user,
  firstRun = false,
  onUploadStatus,
  onUploadProgress,
  onComplete,
  createMeasurementUploadSession,
  completeMeasurementUploadSession,
  failMeasurementUploadSession,
  analytics,
  subjectId = null,
  audioMuted = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { assessment, clearAssessment, resetAssessment, updateAssessment } =
    useSixMinuteWalkAssessment(user?.uid);

  const restartAssessment = useCallback(() => {
    resetAssessment();
    navigate(SIX_MINUTE_WALK_BASE_PATH, { replace: true });
  }, [navigate, resetAssessment]);

  const startAssessment = useCallback(() => {
    const nextAssessment = resetAssessment();
    trackEvent("six_minute_walk_opened", {
      assessmentId: nextAssessment.assessmentId,
    });
    navigate(`${SIX_MINUTE_WALK_BASE_PATH}/pre`, { replace: true });
  }, [navigate, resetAssessment]);

  const continueAssessment = useCallback(() => {
    navigate(resolveResumePath(assessment), { replace: true });
  }, [assessment, navigate]);

  const handlePreUploadStatus = useCallback(
    (event) => {
      const statusValue = getStatusValue(event);
      const uploadId = getUploadId(event);
      const variant = event?.variant || "RecordingSixMinuteWalk";
      const normalizedEvent = {
        ...(event && typeof event === "object" ? event : {}),
        uploadId,
        variant,
        suppressTaskCompletion: true,
      };

      onUploadStatus?.(normalizedEvent);

      if (statusValue !== "uploading" || !uploadId) {
        return;
      }

      updateAssessment((currentAssessment) => ({
        ...currentAssessment,
        preMeasurementId: uploadId,
      }));
      navigate(`${SIX_MINUTE_WALK_BASE_PATH}/walk`, { replace: true });
    },
    [navigate, onUploadStatus, updateAssessment],
  );

  const handlePostRecordingStarted = useCallback(
    ({ startedAt }) => {
      updateAssessment((currentAssessment) => {
        const returnedAtMs = currentAssessment?.returnedAt
          ? Date.parse(currentAssessment.returnedAt)
          : null;
        const startedAtMs = startedAt ? Date.parse(startedAt) : null;

        return {
          ...currentAssessment,
          postResume: null,
          postRecordingStartedAt: startedAt || null,
          recoveryDelayMs:
            Number.isFinite(returnedAtMs) && Number.isFinite(startedAtMs)
              ? Math.max(0, startedAtMs - returnedAtMs)
              : null,
        };
      });
    },
    [updateAssessment],
  );

  const handlePostComplete = useCallback(
    (event) => {
      const statusValue = getStatusValue(event);
      const uploadId = getUploadId(event);
      const variant = event?.variant || "RecordingSixMinuteWalk";
      const normalizedEvent = {
        ...(event && typeof event === "object" ? event : {}),
        uploadId,
        variant,
      };

      onUploadStatus?.(normalizedEvent);

      if (statusValue !== "uploading" || !uploadId) {
        return;
      }

      clearAssessment();
      navigate(
        `/result?variant=${encodeURIComponent(
          variant,
        )}&docid=${encodeURIComponent(uploadId)}&context=${encodeURIComponent(
          RECORDING_MODES.SIX_MINUTE_WALK,
        )}&tab=${encodeURIComponent(RECORDING_MODES.SIX_MINUTE_WALK)}`,
        { replace: true },
      );
    },
    [clearAssessment, navigate, onUploadStatus],
  );

  const preWorkflow = useMemo(
    () => ({
      assessmentType: RECORDING_MODES.SIX_MINUTE_WALK,
      assessmentId: assessment.assessmentId,
      stage: "pre",
      stageLabel: "baseline",
    }),
    [assessment.assessmentId],
  );

  const postWorkflow = useMemo(
    () => ({
      assessmentType: RECORDING_MODES.SIX_MINUTE_WALK,
      assessmentId: assessment.assessmentId,
      stage: "post",
      stageLabel: "recovery",
      pairedMeasurementId: assessment.preMeasurementId,
      preMeasurementId: assessment.preMeasurementId,
      walk: assessment.walk,
      returnedAt: assessment.returnedAt,
      postRecordingStartedAt: assessment.postRecordingStartedAt,
      recoveryDelayMs: assessment.recoveryDelayMs,
    }),
    [
      assessment.assessmentId,
      assessment.postRecordingStartedAt,
      assessment.preMeasurementId,
      assessment.recoveryDelayMs,
      assessment.returnedAt,
      assessment.walk,
    ],
  );

  const sharedPodProps = {
    auth,
    storage,
    db,
    user,
    onUploadProgress,
    onComplete,
    createMeasurementUploadSession,
    completeMeasurementUploadSession,
    failMeasurementUploadSession,
    analytics,
    subjectId,
    showLiveChart: false,
    enablePixelSignal: false,
    enableGrid3x3Pixel: false,
    audioMuted,
    appMode: RECORDING_MODES.SIX_MINUTE_WALK,
    variant: "RecordingSixMinuteWalk",
    navigateToResultOnUploadStart: false,
  };

  const shouldAutoStartPostRecording =
    location.state?.autoStartPost === true ||
    hasPostRecordingContinueIntent(assessment);
  const shouldShowPostRecordingResume =
    !shouldAutoStartPostRecording && hasActivePostRecordingResume(assessment);

  const continueToPostRecording = useCallback(() => {
    updateAssessment((currentAssessment) => ({
      ...currentAssessment,
      postResume: {
        ...(currentAssessment?.postResume || {}),
        continuedAt: new Date().toISOString(),
      },
    }));
    navigate(`${SIX_MINUTE_WALK_BASE_PATH}/post`, {
      replace: true,
      state: { autoStartPost: true },
    });
  }, [navigate, updateAssessment]);

  return (
    <Routes>
      <Route
        index
        element={
          <SixMinuteWalkIntroCard
            assessment={assessment}
            onStart={startAssessment}
            onContinue={continueAssessment}
            onRestart={restartAssessment}
          />
        }
      />

      <Route
        path="pre/*"
        element={
          <RemotePodLoader
            load={loadRecordingPod}
            componentProps={{
              ...sharedPodProps,
              firstRun,
              recordingDurationSeconds: PRE_WALK_RECORDING_DURATION_SECONDS,
              phaseSchedule: PRE_WALK_PHASE_SCHEDULE,
              workflow: preWorkflow,
              onUploadStatus: handlePreUploadStatus,
            }}
          />
        }
      />

      <Route
        path="walk"
        element={
          <SixMinuteWalkWalkStage
            assessment={assessment}
            setAssessment={updateAssessment}
            audioMuted={audioMuted}
          />
        }
      />

      <Route
        path="post/*"
        element={
          assessment?.walk && assessment?.preMeasurementId ? (
            shouldShowPostRecordingResume ? (
              <SixMinuteWalkWalkStage
                assessment={assessment}
                setAssessment={updateAssessment}
                audioMuted={audioMuted}
                resumePostRecording
                onContinueToPost={continueToPostRecording}
              />
            ) : shouldAutoStartPostRecording ? (
              <RemotePodLoader
                load={loadRecordingPod}
                componentProps={{
                  ...sharedPodProps,
                  firstRun: false,
                  recordingDurationSeconds:
                    POST_WALK_RECORDING_DURATION_SECONDS,
                  phaseSchedule: POST_WALK_PHASE_SCHEDULE,
                  workflow: postWorkflow,
                  onRecordingStarted: handlePostRecordingStarted,
                  onUploadStatus: handlePostComplete,
                }}
              />
            ) : (
              <Navigate to={`${SIX_MINUTE_WALK_BASE_PATH}/walk`} replace />
            )
          ) : (
            <Navigate to={SIX_MINUTE_WALK_BASE_PATH} replace />
          )
        }
      />

      <Route
        path="*"
        element={<Navigate to={SIX_MINUTE_WALK_BASE_PATH} replace />}
      />
    </Routes>
  );
});
